import { useCallback, useEffect, useState } from 'react';
import { useGameConnection } from './lib/socket';
import { clearSession, loadSession } from './lib/session';
import { soundBoard } from './lib/sound';
import type { HostState, OverlayState, PlayerState } from './lib/types';
import { HostScreen } from './views/HostView';
import { JoinView } from './views/JoinView';
import { Landing } from './views/Landing';
import { OverlayScreen } from './views/OverlayView';
import { PlayerScreen } from './views/PlayerView';

type Route =
  | { kind: 'landing' }
  | { kind: 'player'; code: string }
  | { kind: 'host'; code: string; token: string | null }
  | { kind: 'overlay'; code: string; key: string | null };

/**
 * Lädt Rollenbilder und Rahmen bereits beim Öffnen der Anwendung in den Browser-Cache.
 * Dadurch müssen sie beim ersten Rollenaufruf nicht mehr sichtbar nachgeladen werden.
 */
function preloadGameImages(): void {
  const frameNames = [
    'demon',
    'ghost',
    'possessed',
    'villager',
    'vampire',
    'witch',
    'priest',
    'seer',
    'fool',
    'cursed',
    'vampire-huntress',
    'lovers',
  ];

  const roleNames = [
    'VILLAGER',
    'SEER',
    'WITCH',
    'PRIEST',
    'VAMPIRE_HUNTRESS',
    'DRUNK',
    'LOVERS',
    'VAMPIRE',
    'FOOL',
    'CURSED',
    'DEMON',
    'GHOST',
  ];

  const urls = [
    ...frameNames.map((name) => `/frames/${name}.webp?v=7`),
    ...roleNames.map((role) => `/roles/B_${role}.png?v=3`),
    '/banner.png?v=7',
    '/frame.png?v=6',
    '/Lobby.png',
    '/wins/W_VILLAGE.jpg?v=5',
    '/wins/W_VAMPIRE.jpg?v=5',
    '/wins/W_LOVERS.jpg?v=5',
    '/wins/W_FOOL.jpg?v=5',
    '/wins/W_DEMON.jpg?v=5',
  ];

  const load = () => {
    for (const url of urls) {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
    }
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(load);
  } else {
    setTimeout(load, 100);
  }
}

/** Wege durch die Anwendung: /, /game/<CODE>, /game/<CODE>/host#token, /game/<CODE>/overlay?key= */
function readRoute(): Route {
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (parts[0] !== 'game' || !parts[1]) return { kind: 'landing' };
  const code = parts[1].toUpperCase();
  if (parts[2] === 'host') return { kind: 'host', code, token: window.location.hash.slice(1) || null };
  if (parts[2] === 'overlay') {
    return { kind: 'overlay', code, key: new URLSearchParams(window.location.search).get('key') };
  }
  return { kind: 'player', code };
}

export function App() {
  const [route, setRoute] = useState<Route>(readRoute);

  useEffect(() => {
    preloadGameImages();
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(readRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Klang darf erst nach einer Nutzeraktion starten.
  useEffect(() => {
    const wake = () => void soundBoard.unlock();
    window.addEventListener('pointerdown', wake, { once: true });
    window.addEventListener('keydown', wake, { once: true });
    return () => {
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
    };
  }, []);

  if (route.kind === 'landing') return <Landing />;
  if (route.kind === 'host') return <Host code={route.code} token={route.token} />;
  if (route.kind === 'overlay') return <Overlay code={route.code} overlayKey={route.key} />;
  return <Player code={route.code} />;
}

function Host({ code, token }: { code: string; token: string | null }) {
  const connection = useGameConnection<HostState>(code, 'host', token);
  if (!token) {
    return (
      <main className="page">
        <p>In diesem Link fehlt der Schlüssel zum Regiepult. Bitte den vollständigen Link aus der Eröffnung verwenden.</p>
      </main>
    );
  }
  return <HostScreen connection={connection} cameraToken={token} />;
}

function Overlay({ code, overlayKey }: { code: string; overlayKey: string | null }) {
  const connection = useGameConnection<OverlayState>(code, 'overlay', overlayKey);
  if (!overlayKey) {
    return (
      <div className="overlay-root">
        <div className="overlay-card bottom">In der OBS-Adresse fehlt der Schlüssel (…/overlay?key=…).</div>
      </div>
    );
  }
  return <OverlayScreen connection={connection} cameraToken={overlayKey} />;
}

function Player({ code }: { code: string }) {
  const [session, setSession] = useState(() => loadSession(code));
  const connection = useGameConnection<PlayerState>(code, 'player', session?.token ?? null);

  const leave = useCallback(() => {
    clearSession(code);
    setSession(null);
  }, [code]);

  if (!session) return <JoinView code={code} onJoined={() => setSession(loadSession(code))} />;
  return <PlayerScreen connection={connection} onLeave={leave} cameraToken={session.token} />;
}
